import { createLogger } from '@/shared/lib/logger';
const logger = createLogger('S3Upload');

const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [1000, 2000, 4000];

// Headers signed into the presigned URL must not be re-added unsigned
// (S3/R2 returns SignatureDoesNotMatch). Mirror what the server signs.
const UNSAFE_HEADERS = new Set([
  'Host',
  'Connection',
  'Content-Length',
  'Accept-Encoding',
  'User-Agent',
  'Referer',
  'Cookie',
]);

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface PresignedConfig {
  method: 'PUT';
  url: string;
  headers: Record<string, string>;
}

function performPut(
  file: File,
  config: PresignedConfig,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open(config.method, config.url);
    Object.entries(config.headers).forEach(([key, value]) => {
      if (!UNSAFE_HEADERS.has(key)) {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.send(file);
  });
}

// Presigned PUT against R2 with bounded retry — matches Apple S3Uploader and
// Android S3UploadService (3 attempts, 1s/2s/4s backoff). No pause/resume.
export async function uploadWithRecovery(
  file: File,
  _legacyUrl: string,
  config: PresignedConfig,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      logger.info(`S3 PUT attempt ${attempt + 1}, size=${file.size}`);
      await performPut(file, config, onProgress);
      return;
    } catch (error) {
      lastError = error;
      const isLast = attempt === MAX_RETRIES - 1;
      logger.warn(
        `S3 PUT failed${isLast ? ' permanently' : ''}, attempt=${attempt + 1}, size=${file.size}`,
        error
      );
      if (!isLast) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      }
    }
  }

  throw lastError;
}
